/**
 * Topbar — rewritten from scratch on the shared design tokens.
 *
 * The previous version pushed `background: transparent !important`
 * through an inline style to beat the vendor stylesheet, which React
 * silently drops anyway. Nothing here overrides anything: topbar.css
 * uses a more specific selector and wins on the cascade.
 *
 * What it holds, left to right: the sidebar toggle, the page slot
 * (global search, or whatever control the current view puts there),
 * then notifications and the account button.
 */

import { useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Bot,
  Menu,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  RotateCw,
  Users,
  X,
} from 'lucide-react';

import { navtoggle } from '../../store/actions/AuthActions';

import { useAuth } from '../../context/AuthContext';
import { useIsMobile } from '../../utils/useIsMobile';
import { useSocket } from '../../context/SocketContext';
import NotificationBell from '../components/NotificationBell/NotificationBell';
import LogoutPage from './Logout';
import TopbarSearch from './TopbarSearch';
import ScreenCapture from '../components/ScreenCapture/ScreenCapture.jsx';
import { refreshCurrentPage } from './pageRefresh';
import { isComingSoon } from '../../utils/comingSoon';
import { useTopbarSlotHost } from './TopbarSlot';

import '../../design/tokens.css';
import './topbar.css';

const initialsOf = (name, email) => {
  const source = String(name || '').trim() || String(email || '').split('@')[0] || '';
  const parts = source.replace(/[._-]+/g, ' ').trim().split(/\s+/).slice(0, 2);
  return parts.map((w) => w[0]).join('').toUpperCase() || 'U';
};

const Header = () => {
  const { user, hasPermission } = useAuth();
  const [userData, setUserData] = useState(null);
  /* openMenuToggle is not taken from the context here any more: it
     derives its value from the sidebar STYLE, not from whether the
     drawer is open, so it could never close one. See the toggle below. */
  const slot = useTopbarSlotHost();
  const dispatch = useDispatch();
  /* The same flag #main-wrapper reads for its `menu-toggle` class. */
  const sideMenu = useSelector((state) => state.sideMenu);

  /* ONE FLAG, TWO OPPOSITE MEANINGS. sideMenu puts `menu-toggle` on
     #main-wrapper, and the stylesheets read it in opposite directions:
     on a phone it SHOWS the overlay drawer, on a desktop it HIDES the
     rail (sidebar.css says as much, at .lms-sidebar-hidden).

     The button was reading it the phone's way at every width, so on a
     desktop it said the reverse of the truth: a hamburger -- "open the
     menu" -- while the sidebar was open, and a close X while it was
     already gone, on a control whose only job at that moment is to
     bring it back. aria-expanded was inverted with it.

     Normalised here, once, so everything below can ask the question
     that actually matters: is the navigation on screen? */
  const isMobile = useIsMobile();
  const navOpen = isMobile ? sideMenu : !sideMenu;

  const [showAccount, setShowAccount] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showChat, setShowChat] = useState(false);

  useEffect(() => {
    if (user) setUserData(user?.user || user);
  }, [user]);

  /* The same unread total the sidebar badge used. */
  const { unreadCounts } = useSocket();
  const unreadTotal = Object.values(unreadCounts || {}).reduce(
    (acc, count) => acc + (count || 0),
    0,
  );

  const isStudent = () =>
    userData?.profiles?.some((p) => (p.label || p.name) === 'Student');

  // Same detection AdminCalendar.jsx uses for "is this a trainer's own
  // session" — a permission check, not a profile-label match, since a
  // trainer's profile label isn't guaranteed to literally be "Trainer".
  const isTrainer = () =>
    !isStudent() && (hasPermission('trainer-calendar', 'view', 'lms') || hasPermission('trainer-calender', 'view', 'lms'));

  /* Who the chat icon is for: anyone signed in. It used to carry the
     sidebar's old test -- a profile labelled exactly 'Student' or
     'Trainer' -- which left admins, CRM and every other profile with no
     way into chat at all, even though they can be members of the same
     direct and group conversations as anyone else and have the same AI
     assistant. Nothing about chat is role-specific, so nothing here
     gates on role. */
  const canUseChat = () => Boolean(userData);

  const isAdmin =
    Boolean(userData?.isSuperAdmin) || String(userData?.profileIds || '').toLowerCase() === 'admin';

  const getProfileRoute = () => (isStudent() ? '/student-profile' : '/user-profile');

  const closeChat = () => setShowChat(false);

  const closeAccount = () => {
    setShowAccount(false);
    setShowSettings(false);
  };

  /* The glyph turns once per click, so the reader sees the press landed
     even on a page that reloads too fast to notice. */
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    if (!refreshing) return undefined;
    const timer = setTimeout(() => setRefreshing(false), 600);
    return () => clearTimeout(timer);
  }, [refreshing]);
  const refreshPage = () => {
    refreshCurrentPage();
    setRefreshing(true);
  };

  const name = userData?.name || userData?.fullName || '';
  const fullName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || name;

  return (
    <div className="header smt-host">
      <div className="smt-topbar">
        {/* Sidebar toggle. It lives in the bar, not in the logo rail, so it
            sits outside the sidebar and reads against the bar's own
            background in every theme. */}
        <div
          className="nav-control"
          role="button"
          tabIndex={0}
          aria-label={navOpen ? 'Hide navigation' : 'Show navigation'}
          /* The icon says which way the click goes; the tooltip says it
             in words for anyone who does not read the glyph that way. */
          title={navOpen ? 'Hide navigation' : 'Show navigation'}
          aria-expanded={navOpen}
          onClick={() => {
            /* navtoggle() flips it; openMenuToggle() only ever sets
               ThemeContext's own menuToggle to true/false from the
               sidebar STYLE, never from this state, so calling it here
               did nothing for the drawer and could not close it. The
               redux flag is the one #main-wrapper reads. */
            dispatch(navtoggle());
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              dispatch(navtoggle());
            }
          }}
        >
          {/* Two different controls wearing one button.

              On a phone the drawer is a layer over the page, so it
              follows the drawer convention: hamburger to summon it, X
              to dismiss it -- X is right there because there genuinely
              is something in the way.

              On a desktop nothing is ever in the way. The rail is part
              of the page and the button folds it away and back, so the
              icon points at what the click will do rather than
              pretending to dismiss something. Nothing to cancel, so no
              cancel icon. */}
          {isMobile ? (
            navOpen ? <X /> : <Menu />
          ) : navOpen ? (
            <PanelLeftClose />
          ) : (
            <PanelLeftOpen />
          )}
        </div>

        {/* The middle of the bar belongs to the current view first: a page
            can mount its own control here through <TopbarSlot>. Nothing
            claims it on most pages, so search takes the space instead. It
            shows only the sources this account may open — a student sees
            none, so it renders nothing for them. */}
        <div className="smt-tb-slot" ref={slot?.setEl} />
        {userData && !isStudent() && !slot?.filled && <TopbarSearch />}

        <div className="smt-tb-right">
          {/* Refresh: reloads the page component only (pageRefresh.js) --
              not the whole app, so the session, the sidebar and anything
              open in the corners stay put. Not on a phone, for the same
              bottom-bar reason as the screenshot button. */}
          {userData && !isMobile && (
            <button
              type="button"
              className={`lms-icon-btn lms-refresh-btn${refreshing ? ' is-spinning' : ''}`}
              onClick={refreshPage}
              aria-label="Refresh this page"
              title="Refresh this page"
            >
              <RotateCw aria-hidden="true" />
            </button>
          )}

          {/* Screenshot: capture, mark up, copy or email. Staff only -- not
              for students (the API refuses them too). Not on a phone --
              the bottom bar has room for three tabs, and phone browsers
              cannot capture the screen. */}
          {userData && !isStudent() && !isMobile && (
            <ScreenCapture taker={{ name: fullName || name, email: userData?.email || '' }} />
          )}

          {/* Chat. It used to be a row inside three different sidebar
              groups; it is one icon here, beside the bell, because it is
              a place you dip into rather than a section you navigate to.
              Everyone signed in gets it -- see canUseChat.

              The three kinds of conversation are one page now, with a
              dropdown in its header to switch between them. The menu here
              still lists all three, because ?type= opens the page on the
              one that was asked for -- picking the kind before you arrive
              is a shorter path than arriving and then picking it. */}
          {canUseChat() && (
            <Dropdown
              align="end"
              show={showChat}
              onToggle={(isOpen) => setShowChat(isOpen)}
              className="smt-chat"
            >
              <Dropdown.Toggle
                as="div"
                className="lms-icon-btn"
                role="button"
                tabIndex={0}
                aria-label={unreadTotal > 0 ? `Chat, ${unreadTotal} unread` : 'Chat'}
                title="Chat"
              >
                <MessageSquare aria-hidden="true" />
                {/* Bottom bar only -- topbar.css hides it elsewhere. */}
                <span className="smt-tb-label">Chat</span>
                {unreadTotal > 0 && <span className="lms-dot" aria-hidden="true" />}
              </Dropdown.Toggle>

              <Dropdown.Menu className="smt-account-menu" popperConfig={{ strategy: 'fixed' }} renderOnMount>
                {/* Admins and students only. The assistant answers about
                    courses, batches and admissions -- a student's own
                    questions, and an admin's to check what it tells them.
                    A trainer's questions are not the ones it can answer,
                    so it is not offered rather than offered and unhelpful.

                    Direct messages and group chats below stay open to
                    everyone; this gate is on the bot alone. */}
                {(isAdmin || isStudent()) && (
                  <Link to="/chat?type=ai" className="dropdown-item" onClick={closeChat}>
                    <Bot width={18} height={18} aria-hidden="true" />
                    <span>AI Assistant</span>
                  </Link>
                )}

                <Link to="/chat?type=direct" className="dropdown-item" onClick={closeChat}>
                  <MessageSquare width={18} height={18} aria-hidden="true" />
                  <span>Trainer chat</span>
                </Link>

                <Link to="/chat?type=group" className="dropdown-item" onClick={closeChat}>
                  <Users width={18} height={18} aria-hidden="true" />
                  <span>Group chat</span>
                  {unreadTotal > 0 && (
                    <span className="smt-chat-count">{unreadTotal}</span>
                  )}
                </Link>
              </Dropdown.Menu>
            </Dropdown>
          )}

          {/* Real, DB-backed notifications (batch add/remove, batch-switch
              request filed/accepted/rejected/reverted) — see NotificationBell. */}
          {isStudent() && <NotificationBell email={userData?.email} recipientType="student" />}
          {isTrainer() && <NotificationBell email={userData?.email} recipientType="trainer" />}


          {/* Account. Inside .smt-tb-right, which is what puts it in the
              bottom bar on a phone: that group leaves the top of the
              screen below 768px and becomes a fixed tab bar, and the
              account is one of the three tabs in it.

              It sat outside this group for a while, which left it alone
              in the topbar on a phone while chat and the bell were at
              the bottom -- two places to reach for the same kind of
              thing. On a desktop nothing moves: this group carries
              margin-left:auto, so the account is still the last item in
              the row.

              topbar.css already had the bottom-bar rules for it --
              .smt-tb-right .smt-account .smt-account-btn and the caret,
              avatar and label rules under it were never removed. */}
          {userData && (
            <Dropdown
              align="end"
              show={showAccount}
              onToggle={(isOpen) => {
                setShowAccount(isOpen);
                if (!isOpen) setShowSettings(false);
              }}
              className="smt-account"
            >
              <Dropdown.Toggle as="div" className="smt-account-btn" role="button" tabIndex={0}>
                <span className="smt-tb-avatar" title={fullName || userData?.email || ''}>
                  {initialsOf(fullName || name, userData?.email)}
                </span>
                <span className="smt-account-id">
                  <b>{fullName || userData?.email}</b>
                  <small>{userData?.profiles?.[0]?.label || 'User'}</small>
                </span>
                <span className="smt-tb-label">Account</span>
                <svg className="smt-account-caret" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </Dropdown.Toggle>

              <Dropdown.Menu className="smt-account-menu" popperConfig={{ strategy: 'fixed' }} renderOnMount>
                <Link to={getProfileRoute()} className="dropdown-item" onClick={closeAccount}>
                  <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx={12} cy={7} r={4} />
                  </svg>
                  <span>Profile</span>
                </Link>

                {/* Everyone but an administrator can ask for another role;
                    an administrator decides on the dashboard's Access tab. */}
                {!isAdmin && !isComingSoon('access-center') && (
                  <Link to="/access-center" className="dropdown-item" onClick={closeAccount}>
                    <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx={7.5} cy={15.5} r={5.5} />
                      <path d="m21 2-9.6 9.6" />
                      <path d="m15.5 7.5 3 3L22 7l-3-3" />
                    </svg>
                    <span>Request access</span>
                  </Link>
                )}

                {hasPermission('student-users', 'delete', 'lms') && (
                  <Link to="/student-users" className="dropdown-item" onClick={closeAccount}>
                    <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx={9} cy={7} r={4} />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <span>Student Users</span>
                  </Link>
                )}

                {isAdmin && (
                  <Dropdown
                    drop="start"
                    show={showSettings}
                    onToggle={(isOpen) => setShowSettings(isOpen)}
                  >
                    <Dropdown.Toggle as="div" className="dropdown-item smt-account-sub" role="button" tabIndex={0}>
                      <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c0 .7.4 1.3 1 1.51V12a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                      <span>Settings</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="smt-account-menu">
                      <Link to="/setting/list-opreation" className="dropdown-item" onClick={closeAccount}>Operations</Link>
                      <Link to="/setting/list-modules" className="dropdown-item" onClick={closeAccount}>Modules</Link>
                      <Link to="/setting/operationstomodules" className="dropdown-item" onClick={closeAccount}>Operations To Modules</Link>
                      <Link to="/setting/mta-list" className="dropdown-item" onClick={closeAccount}>Modules To Apps</Link>
                      <Link to="/setting/list-apps" className="dropdown-item" onClick={closeAccount}>App</Link>
                      <Link to="/setting/list-app-to-role" className="dropdown-item" onClick={closeAccount}>Apps To Role</Link>
                      <Link to="/setting/List-user" className="dropdown-item" onClick={closeAccount}>User</Link>
                      <Link to="/email-templates" className="dropdown-item" onClick={closeAccount}>Email Templates</Link>
                      <Link to="/mailer-metadatas" className="dropdown-item" onClick={closeAccount}>Mailer Metas</Link>
                      <Link to="/email-event-configs" className="dropdown-item" onClick={closeAccount}>Email Event Configs</Link>
                    </Dropdown.Menu>
                  </Dropdown>
                )}

                <div onClick={closeAccount}>
                  <LogoutPage />
                </div>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
